import {
  AbsoluteCenter,
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  Heading,
  HStack,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon, InfoOutlineIcon, StarIcon } from '@chakra-ui/icons';
import {
  deleteMonitoringApi,
  buildMonitoringSlidesApi,
  downloadPortfolioMonitoringExcelApi,
  fetchMonitoringApi,
  getMonitoringApi,
  getMonitoringListApi,
  saveMonitoringApi,
} from 'api/monitoringApi';
import DataTable from 'components/DataTable';
import { TwDatePicker } from 'components/DatePicker';
import { HelpLink } from 'components/HelpLink';
import TwMenu from 'components/menu/Menu';
import { currencyFormat, percentageFormat } from 'components/Number';
import Card from 'components/card/Card';
import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CockpitCustomerContext } from 'context/CockpitCustomerContext';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { getLatestScorecardConfig } from 'api/tearsheetScorecardConfigApi';
import {
  applyLiquidityCashToResultRows,
  buildMonitoringScorecardViewModel,
  MONITORING_PARAM_TOOLTIPS,
  patchCompanyScoreSnapshot,
  recalculateMonitoringCompanyScoreFromParams,
} from './monitoringCompanyScore';
import MonitoringKpiStrip from './monitoringKpiStrip';
import {
  buildMonitoringDealPath,
  downloadMonitoringExcel,
} from './exportMonitoringExcel';
import { downloadMonitoringSlides } from './exportMonitoringSlides';
import {
  MONITORING_RISK_INPUT_DOLLAR_FIELDS,
  MONITORING_RISK_INPUT_SCORE_FIELDS,
  formatMonitoringRiskInputDisplay,
} from './monitoringRiskInputFields';
import {
  buildMonitoringRoasQuickViewRow,
  displayMonitoringCovenantKpi,
  filterMonitoringCovenantRows,
  formatMonitoringConstraintValue,
  formatMonitoringCovenantsHeading,
  formatMonitoringRoasCac,
  formatMonitoringRoasRowCellValue,
  formatMonitoringRoasCovenantBasis,
  monitoringBackToListNeedsNavigate,
  monitoringListPath,
  monitoringRoasCacBackgroundColor,
  monitoringRoasCacFails,
  monitoringRoasCacValueColor,
  monitoringRoasCellBackgroundColor,
  monitoringRoasCellTooltip,
  monitoringRoasCellValueColor,
  monitoringRoasRowView,
  monitoringRowDividerProps,
} from './monitoringHelpers';

const ROAS_COHORT_COL_W = 140;
const ROAS_BASIS_COL_W = 96;
const ROAS_CAC_COL_W = 88;
const ROAS_CAC_STICKY_LEFT = ROAS_COHORT_COL_W + ROAS_BASIS_COL_W;
const ROAS_STAGE_STICKY_LEFT = ROAS_CAC_STICKY_LEFT + ROAS_CAC_COL_W;

const columnHelper = createColumnHelper();

function ParameterLabel({ label, tooltip }) {
  return (
    <HStack spacing={1}>
      <Text as="span">{label}</Text>
      {tooltip ? (
        <Tooltip label={tooltip} hasArrow placement="top" maxW="320px">
          <InfoOutlineIcon boxSize={3} color="gray.400" cursor="help" />
        </Tooltip>
      ) : null}
    </HStack>
  );
}

function parseOptionalNumber(raw) {
  if (raw == null || raw === '') {
    return null;
  }
  const n = Number(String(raw).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** Live-format while typing: thousand separators, at most 2 fraction digits. */
function formatRiskInputLive(raw) {
  if (raw == null) {
    return '';
  }
  let text = String(raw);
  if (text === '') {
    return '';
  }
  text = text.replace(/[^\d.-]/g, '');
  const negative = text.startsWith('-');
  if (negative) {
    text = text.slice(1);
  }
  text = text.replace(/-/g, '');
  const firstDot = text.indexOf('.');
  let intDigits = '';
  let fracDigits = null;
  let trailingDot = false;
  if (firstDot === -1) {
    intDigits = text.replace(/\D/g, '');
  } else {
    intDigits = text.slice(0, firstDot).replace(/\D/g, '');
    const after = text.slice(firstDot + 1).replace(/\D/g, '').slice(0, 2);
    fracDigits = after;
    trailingDot = text[firstDot] === '.' && after.length === 0;
  }
  const intGrouped = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  let out = negative ? '-' : '';
  out += intGrouped;
  if (trailingDot) {
    out += '.';
  } else if (fracDigits != null) {
    out += `.${fracDigits}`;
  }
  return out;
}

function riskInputDigitIndexBeforeCaret(value, caret) {
  return String(value)
    .slice(0, Math.max(0, caret))
    .replace(/[^\d]/g, '').length;
}

function riskInputCaretFromDigitIndex(formatted, digitIndex) {
  if (digitIndex <= 0) {
    return formatted.startsWith('-') ? 1 : 0;
  }
  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) {
      seen += 1;
      if (seen >= digitIndex) {
        return i + 1;
      }
    }
  }
  return formatted.length;
}

const CollapsibleSectionHeader = ({ title, isOpen, onToggle, actions }) => (
  <HStack
    justifyContent="space-between"
    alignItems="flex-start"
    mb={isOpen ? 4 : 0}
    w="100%"
  >
    <HStack
      spacing={1}
      cursor="pointer"
      userSelect="none"
      w="fit-content"
      onClick={onToggle}
    >
      {isOpen ? (
        <ChevronDownIcon boxSize={4} color="gray.500" />
      ) : (
        <ChevronRightIcon boxSize={4} color="gray.500" />
      )}
      <Heading size="sm">{title}</Heading>
    </HStack>
    {actions}
  </HStack>
);

function checkBadge(check) {
  if (check === 'pass') {
    return <Badge colorScheme="green">Pass</Badge>;
  }
  if (check === 'fail') {
    return <Badge colorScheme="red">Fail</Badge>;
  }
  return <Badge>N/A</Badge>;
}

function formatRowValue(kpi, value) {
  if (value == null || value === '') {
    return '—';
  }
  if (
    kpi?.includes('%') ||
    kpi?.toLowerCase().includes('mom increase')
  ) {
    return percentageFormat(Number(value) / 100, { maximumFractionDigits: 2 });
  }
  return currencyFormat(Number(value), { maximumFractionDigits: 0 });
}

const Monitoring = () => {
  const { customerId, monitoringId } = useParams();
  const { customer } = useContext(CockpitCustomerContext);
  const navigate = useNavigate();
  const toast = useToast();
  const {
    isOpen: isSaveOpen,
    onOpen: onSaveOpen,
    onClose: onSaveClose,
  } = useDisclosure();

  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [reports, setReports] = useState([]);
  const [showCreateMode, setShowCreateMode] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [fetchResult, setFetchResult] = useState(null);
  const [saveName, setSaveName] = useState('');
  const [riskScoreOpen, setRiskScoreOpen] = useState(true);
  const [riskInputsOpen, setRiskInputsOpen] = useState(false);
  const [riskInputsDraft, setRiskInputsDraft] = useState(null);
  const [editingParamKey, setEditingParamKey] = useState(null);
  const [editingParamText, setEditingParamText] = useState('');
  const [covenantsOpen, setCovenantsOpen] = useState(true);
  const [roasCohortOpen, setRoasCohortOpen] = useState(true);
  const [roasTableView, setRoasTableView] = useState('quick');
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [isDownloadingPortfolioExcel, setIsDownloadingPortfolioExcel] =
    useState(false);
  const [isDownloadingSlides, setIsDownloadingSlides] = useState(false);
  const [isBuildingSlides, setIsBuildingSlides] = useState(false);
  const [fetchAsOfDate, setFetchAsOfDate] = useState(() =>
    moment().format('YYYY-MM-DD')
  );
  const [scorecardConfig, setScorecardConfig] = useState(null);

  const loadReports = useCallback(async () => {
    const list = await getMonitoringListApi({ customerId });
    setReports(Array.isArray(list) ? list : []);
  }, [customerId]);

  useEffect(() => {
    let cancelled = false;
    getLatestScorecardConfig()
      .then((config) => {
        if (!cancelled) {
          setScorecardConfig(config);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        if (monitoringId) {
          const saved = await getMonitoringApi({ customerId, monitoringId });
          if (cancelled) return;
          const payload = saved?.payloadJson
            ? JSON.parse(saved.payloadJson)
            : null;
          setFetchResult(payload);
          if (payload?.riskInputs) {
            setRiskInputsDraft(payload.riskInputs);
          }
          setSaveName(saved?.name || '');
          setIsSaved(true);
          setShowCreateMode(true);
        } else {
          await loadReports();
          if (cancelled) return;
          setShowCreateMode(false);
          setIsSaved(false);
          setFetchResult(null);
        }
      } catch (error) {
        if (!cancelled) {
          toast({
            title: 'Failed to load monitoring',
            description: error?.message || String(error),
            status: 'error',
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, monitoringId, loadReports, toast]);

  const enterCreateMode = () => {
    setShowCreateMode(true);
    setIsSaved(false);
    setFetchResult(null);
    setSaveName('');
    setRiskInputsDraft(null);
    setRiskInputsOpen(false);
    setFetchAsOfDate(moment().format('YYYY-MM-DD'));
  };

  const backToList = () => {
    const listPath = monitoringListPath(customerId);
    const willNavigate = monitoringBackToListNeedsNavigate(monitoringId);
    // New Monitoring keeps the list URL, so navigate() alone is a no-op — always reset create mode.
    setShowCreateMode(false);
    setIsSaved(false);
    setFetchResult(null);
    setSaveName('');
    setRiskInputsDraft(null);
    setRiskInputsOpen(false);
    setEditingParamKey(null);
    if (willNavigate) {
      navigate(listPath);
    } else {
      loadReports().catch(() => {});
    }
  };

  const onFetch = async () => {
    try {
      setIsFetching(true);
      setIsBuildingSlides(false);
      const data = { asOfDate: fetchAsOfDate };
      if (riskInputsDraft) {
        data.riskInputs = riskInputsDraft;
      }
      const result = await fetchMonitoringApi({ customerId, data });
      setFetchResult(result);
      if (result?.riskInputs) {
        setRiskInputsDraft(result.riskInputs);
      }
      setIsBuildingSlides(true);
      try {
        const slidesResp = await buildMonitoringSlidesApi({
          customerId,
          payload: result,
        });
        if (slidesResp?.slidesSnapshot) {
          setFetchResult((prev) =>
            prev
              ? { ...prev, slidesSnapshot: slidesResp.slidesSnapshot }
              : prev
          );
        }
      } catch (slidesError) {
        toast({
          title: 'Slides build failed',
          description:
            slidesError?.response?.data?.error ||
            slidesError?.message ||
            String(slidesError),
          status: 'warning',
          duration: 8000,
          isClosable: true,
        });
      } finally {
        setIsBuildingSlides(false);
      }
    } catch (error) {
      toast({
        title: 'Monitoring fetch failed',
        description: error?.response?.data?.error || error?.message || String(error),
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setIsFetching(false);
    }
  };

  const onRecalcCompanyScore = () => {
    if (!fetchResult) {
      return;
    }
    const nextRiskInputs = {
      ...(fetchResult.riskInputs || {}),
      ...(riskInputsDraft || {}),
    };
    const scored = recalculateMonitoringCompanyScoreFromParams(
      nextRiskInputs,
      fetchResult.cashflowScorecard || [],
      scorecardConfig,
    );
    const minCash = fetchResult.dealConstraints?.minCashBalance;
    const nextSlidesSnapshot = fetchResult.slidesSnapshot
      ? {
          ...fetchResult.slidesSnapshot,
          companyScore: patchCompanyScoreSnapshot(
            fetchResult.slidesSnapshot.companyScore,
            scored,
          ),
        }
      : fetchResult.slidesSnapshot;
    setFetchResult({
      ...fetchResult,
      riskInputs: nextRiskInputs,
      cashflowScorecard: scored.cashflowScorecard,
      riskScoreFinalPct: scored.riskScoreFinalPct,
      riskScoreFinalLabel: scored.riskScoreFinalLabel,
      scorecardFinalScore: scored.scorecardFinalScore,
      slidesSnapshot: nextSlidesSnapshot,
      resultRows: applyLiquidityCashToResultRows(
        fetchResult.resultRows,
        scored.liquidityCash,
        minCash,
      ),
    });
    setRiskInputsDraft(nextRiskInputs);
    setRiskScoreOpen(true);
  };

  const onSave = async () => {
    if (!saveName.trim() || !fetchResult) {
      return;
    }
    try {
      setIsSaving(true);
      const saved = await saveMonitoringApi({
        customerId,
        data: { name: saveName.trim(), payload: fetchResult },
      });
      setIsSaved(true);
      onSaveClose();
      await loadReports();
      navigate(`/customers/${customerId}/performance/monitoring/${saved.id}`);
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error?.message || String(error),
        status: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteMonitoringApi({ customerId, monitoringId: id });
      await loadReports();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error?.message || String(error),
        status: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('sequenceNumber', {
        header: '#',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('name', {
        header: 'NAME',
        cell: (info) => info.getValue() || '—',
      }),
      columnHelper.accessor('asOfDate', {
        header: 'AS OF',
        cell: (info) => {
          const value = info.getValue();
          return value ? moment(value, 'YYYY-MM-DD').format('MMM D, YYYY') : '—';
        },
      }),
      columnHelper.accessor('createdAt', {
        header: 'CREATED',
        cell: (info) =>
          info.getValue()
            ? moment(info.getValue()).format('MMM D, YYYY HH:mm')
            : '—',
      }),
      columnHelper.accessor('finalScore', {
        header: 'SCORE',
        cell: (info) => {
          const score = info.getValue();
          return score != null ? score : '—';
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => (
          <TwMenu
            items={[
              {
                label: 'Delete',
                onClick: (event) => {
                  event.stopPropagation();
                  onDelete(info.row.original.id);
                },
              },
            ]}
          />
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId]
  );

  const scorecardRows = fetchResult?.cashflowScorecard || [];
  const companyScoreView = useMemo(
    () => buildMonitoringScorecardViewModel(scorecardRows, riskInputsDraft, scorecardConfig),
    [scorecardRows, riskInputsDraft, scorecardConfig],
  );
  const resultRows = filterMonitoringCovenantRows(fetchResult?.resultRows).map(
    (row) => ({
      ...row,
      kpi: displayMonitoringCovenantKpi(row?.kpi),
    }),
  );
  const roasRows = fetchResult?.roasCohort?.rows || [];
  const roasPeriodLabels = fetchResult?.roasCohort?.periodLabels || [];
  const covenantsHeading = formatMonitoringCovenantsHeading({
    targetCohortMonth: fetchResult?.targetCohortMonth,
    closestRepaymentDate: fetchResult?.closestRepaymentDate,
  });
  const asOfDate = fetchResult?.asOfDate;
  const quickPrevMonthLabel = asOfDate
    ? moment(asOfDate).subtract(1, 'month').format('MMM YYYY')
    : 'Previous';
  const quickCurrMonthLabel = asOfDate
    ? moment(asOfDate).format('MMM YYYY')
    : 'Current';
  const roasQuickRows = useMemo(
    () =>
      roasRows.map((row) => ({
        row,
        quick: buildMonitoringRoasQuickViewRow(row, asOfDate),
      })),
    [roasRows, asOfDate],
  );

  const onDownloadExcel = async () => {
    if (!fetchResult) {
      return;
    }
    try {
      setIsDownloadingExcel(true);
      const scorecardConfig = await getLatestScorecardConfig().catch(() => null);
      await downloadMonitoringExcel({
        fetchResult,
        riskInputs: riskInputsDraft ?? fetchResult.riskInputs,
        reportName: saveName?.trim() || undefined,
        scorecardConfig,
        companyName: customer?.companyName,
      });
    } catch (error) {
      toast({
        title: 'Excel download failed',
        description: error?.message || String(error),
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const onDownloadPortfolioExcel = async () => {
    try {
      setIsDownloadingPortfolioExcel(true);
      await downloadPortfolioMonitoringExcelApi({
        asOfDate: fetchAsOfDate || undefined,
      });
    } catch (error) {
      toast({
        title: 'Portfolio Excel download failed',
        description: error?.message || String(error),
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setIsDownloadingPortfolioExcel(false);
    }
  };

  const onDownloadSlides = async () => {
    if (!fetchResult) {
      return;
    }
    try {
      setIsDownloadingSlides(true);
      await downloadMonitoringSlides({
        customerId,
        monitoringId: isSaved ? monitoringId : undefined,
        fetchResult,
      });
    } catch (error) {
      toast({
        title: 'Slides download failed',
        description: error?.message || String(error),
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setIsDownloadingSlides(false);
    }
  };

  const monitoringDealId = fetchResult?.dealConstraints?.dealId;
  const monitoringDealPath = buildMonitoringDealPath(customerId, monitoringDealId);
  const monitoringDealLabel =
    fetchResult?.dealConstraints?.dealName ||
    fetchResult?.dealConstraints?.dealId ||
    '—';

  if (isLoading && !showCreateMode) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  if (!showCreateMode) {
    return (
      <Box maxW="container.xl">
        <Card>
          <Flex justifyContent="space-between" alignItems="center" mb={8}>
            <Heading variant="sectionHeader" borderColor="twelve.green.300">
              Monitoring
            </Heading>
            <HStack spacing={2}>
              <Button
                variant="outline"
                onClick={onDownloadPortfolioExcel}
                isLoading={isDownloadingPortfolioExcel}
              >
                Download portfolio Excel
              </Button>
              <Button variant="brand" onClick={enterCreateMode}>
                + New Monitoring
              </Button>
            </HStack>
          </Flex>
          <Box mx={-5}>
            <DataTable
              data={reports}
              columns={columns}
              rowHeight="80px"
              onRowClick={(row) =>
                navigate(
                  `/customers/${customerId}/performance/monitoring/${row.id}`
                )
              }
            />
          </Box>
        </Card>
      </Box>
    );
  }

  return (
    <Box maxW="container.xl">
      <Card>
        <Flex justifyContent="space-between" alignItems="center" mb={6} gap={4}>
          <HStack spacing={3} align="center">
            <Heading variant="sectionHeader" borderColor="twelve.green.300">
              {isSaved ? saveName || 'Monitoring report' : 'New Monitoring'}
            </Heading>
            <HelpLink
              sectionId="customer-monitoring-slides-overview"
              variant="subtle"
              fontSize="sm"
            >
              How slides are built
            </HelpLink>
          </HStack>
          <Flex gap={2}>
            <Button variant="outline" onClick={backToList}>
              Back to list
            </Button>
            {!isSaved && (
              <HStack spacing={3} align="center">
                <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                  As of
                </Text>
                <TwDatePicker
                  w={160}
                  value={fetchAsOfDate}
                  onChange={setFetchAsOfDate}
                  isDisabled={isFetching || isBuildingSlides}
                  maxDate={moment().format('YYYY-MM-DD')}
                />
                <Button
                  variant="brand"
                  onClick={onFetch}
                  isLoading={isFetching}
                  isDisabled={isBuildingSlides}
                >
                  Fetch
                </Button>
              </HStack>
            )}
            {!isSaved && fetchResult && (
              <Button variant="brand" onClick={onSaveOpen}>
                Save
              </Button>
            )}
            <Button
              variant="outline"
              onClick={onDownloadPortfolioExcel}
              isLoading={isDownloadingPortfolioExcel}
              isDisabled={isFetching}
            >
              Download portfolio Excel
            </Button>
            {fetchResult && (
              <Button
                variant="outline"
                onClick={onDownloadExcel}
                isLoading={isDownloadingExcel}
                isDisabled={isFetching}
              >
                Download Excel
              </Button>
            )}
            {fetchResult && (
              <Button
                variant="outline"
                onClick={onDownloadSlides}
                isLoading={isDownloadingSlides}
                isDisabled={
                  !fetchResult.slidesSnapshot || isFetching || isBuildingSlides
                }
              >
                Download slides
              </Button>
            )}
          </Flex>
        </Flex>

        {isBuildingSlides && (
          <HStack spacing={2} mb={4}>
            <Spinner size="sm" />
            <Text fontSize="sm" color="gray.600">
              Building slides…
            </Text>
          </HStack>
        )}

        {fetchResult && (
          <Box mb={4}>
            <Text fontSize="sm" color="gray.600">
              Deal:{' '}
              {monitoringDealPath ? (
                <Link
                  as={RouterLink}
                  to={monitoringDealPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="brand.500"
                  fontWeight="medium"
                >
                  {monitoringDealLabel}
                </Link>
              ) : (
                monitoringDealLabel
              )}
              {' · '}
              Target cohort: {fetchResult.targetCohortMonth || '—'}
              {' · '}
              As of: {fetchResult.asOfDate || '—'}
            </Text>
            {fetchResult.riskScoreFinalLabel && (
              <Text mt={1} fontWeight="semibold">
                Company score: {fetchResult.riskScoreFinalLabel}
                {fetchResult.riskScoreFinalPct != null
                  ? ` (${percentageFormat(fetchResult.riskScoreFinalPct, {
                      maximumFractionDigits: 0,
                    })})`
                  : ''}
              </Text>
            )}
          </Box>
        )}

        {!fetchResult && !isFetching && (
          <Text color="gray.600">
            Click Fetch to build a monitoring report from the latest deal,
            marketing spend, Asset Tape exposure, and covenants.
          </Text>
        )}
        {isFetching && (
          <Flex justify="center" py={10}>
            <Spinner />
          </Flex>
        )}
      </Card>

      {fetchResult && !isFetching && (
        <>
          <MonitoringKpiStrip
            fetchResult={fetchResult}
            riskInputsDraft={riskInputsDraft}
          />
          <Card mt={6} variant="outline">
            <CollapsibleSectionHeader
              title="Company Parameters"
              isOpen={riskInputsOpen}
              onToggle={() => setRiskInputsOpen((open) => !open)}
              actions={
                !isSaved ? (
                  <Button
                    size="sm"
                    variant="brand"
                    onClick={onRecalcCompanyScore}
                    isDisabled={isFetching}
                  >
                    Recalc
                  </Button>
                ) : null
              }
            />
            <Collapse in={riskInputsOpen} animateOpacity>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Parameter</Th>
                    <Th w="140px">Value</Th>
                    <Th>Source</Th>
                    <Th>Comment</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {MONITORING_RISK_INPUT_DOLLAR_FIELDS.map((field) => (
                    <Tr key={field.key}>
                      <Td>
                        <ParameterLabel
                          label={field.label}
                          tooltip={MONITORING_PARAM_TOOLTIPS[field.key]}
                        />
                      </Td>
                      <Td w="140px">
                        <Input
                          size="sm"
                          type="text"
                          inputMode="decimal"
                          textAlign="right"
                          maxW="120px"
                          value={
                            editingParamKey === field.key
                              ? editingParamText
                              : formatMonitoringRiskInputDisplay(
                                  riskInputsDraft?.[field.key],
                                )
                          }
                          isDisabled={isSaved}
                          onFocus={(event) => {
                            const current = riskInputsDraft?.[field.key];
                            const formatted = formatMonitoringRiskInputDisplay(current);
                            setEditingParamKey(field.key);
                            setEditingParamText(formatted);
                            requestAnimationFrame(() => {
                              const el = event.target;
                              const end = el.value.length;
                              el.setSelectionRange(end, end);
                            });
                          }}
                          onChange={(event) => {
                            const el = event.target;
                            const caret = el.selectionStart ?? el.value.length;
                            const digitIndex = riskInputDigitIndexBeforeCaret(
                              el.value,
                              caret,
                            );
                            const formatted = formatRiskInputLive(el.value);
                            setEditingParamText(formatted);
                            const parsed = parseOptionalNumber(formatted);
                            const previous = riskInputsDraft?.[field.key];
                            const unchanged =
                              (parsed == null &&
                                (previous == null || previous === undefined)) ||
                              (parsed != null &&
                                previous != null &&
                                Number(parsed) === Number(previous));
                            if (!unchanged) {
                              setRiskInputsDraft({
                                ...(riskInputsDraft || {}),
                                [field.key]: parsed,
                                [field.sourceKey]: 'Manual entry',
                              });
                            } else if (parsed == null && previous != null) {
                              setRiskInputsDraft({
                                ...(riskInputsDraft || {}),
                                [field.key]: null,
                                [field.sourceKey]: 'Manual entry',
                              });
                            }
                            requestAnimationFrame(() => {
                              const nextCaret = riskInputCaretFromDigitIndex(
                                formatted,
                                digitIndex,
                              );
                              el.setSelectionRange(nextCaret, nextCaret);
                            });
                          }}
                          onBlur={() => {
                            const parsed = parseOptionalNumber(editingParamText);
                            const previous = riskInputsDraft?.[field.key];
                            const unchanged =
                              (parsed == null &&
                                (previous == null || previous === undefined)) ||
                              (parsed != null &&
                                previous != null &&
                                Number(parsed) === Number(previous));
                            if (!unchanged) {
                              setRiskInputsDraft({
                                ...(riskInputsDraft || {}),
                                [field.key]: parsed,
                                [field.sourceKey]: 'Manual entry',
                              });
                            }
                            setEditingParamKey(null);
                            setEditingParamText('');
                          }}
                        />
                      </Td>
                      <Td>
                        <Text fontSize="xs" color="gray.500">
                          {riskInputsDraft?.[field.sourceKey] || '—'}
                        </Text>
                      </Td>
                      <Td>
                        <Input
                          size="sm"
                          maxW="280px"
                          placeholder="Comment if changed…"
                          value={riskInputsDraft?.[field.commentKey] || ''}
                          isDisabled={isSaved}
                          onChange={(event) => {
                            setRiskInputsDraft({
                              ...(riskInputsDraft || {}),
                              [field.commentKey]: event.target.value,
                            });
                          }}
                        />
                      </Td>
                    </Tr>
                  ))}
                  {MONITORING_RISK_INPUT_SCORE_FIELDS.map((field) => (
                    <Tr key={field.key}>
                      <Td>
                        <ParameterLabel
                          label={field.label}
                          tooltip={MONITORING_PARAM_TOOLTIPS[field.key]}
                        />
                      </Td>
                      <Td w="140px">
                        <RadioGroup
                          value={
                            riskInputsDraft?.[field.key] != null
                              ? String(riskInputsDraft[field.key])
                              : ''
                          }
                          isDisabled={isSaved}
                          onChange={(value) => {
                            const score = parseOptionalNumber(value);
                            setRiskInputsDraft({
                              ...(riskInputsDraft || {}),
                              [field.key]:
                                score != null ? Math.round(score) : null,
                            });
                          }}
                        >
                          <HStack spacing={4}>
                            <Radio value="1">1</Radio>
                            <Radio value="2">2</Radio>
                            <Radio value="3">3</Radio>
                          </HStack>
                        </RadioGroup>
                      </Td>
                      <Td>
                        <Text fontSize="xs" color="gray.500">
                          Score 1–3
                        </Text>
                      </Td>
                      <Td>
                        <Input
                          size="sm"
                          maxW="280px"
                          placeholder="Comment if changed…"
                          value={riskInputsDraft?.[field.commentKey] || ''}
                          isDisabled={isSaved}
                          onChange={(event) => {
                            setRiskInputsDraft({
                              ...(riskInputsDraft || {}),
                              [field.commentKey]: event.target.value,
                            });
                          }}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Collapse>
          </Card>

          <Card mt={6} variant="outline">
            <CollapsibleSectionHeader
              title="Company Score"
              isOpen={riskScoreOpen}
              onToggle={() => setRiskScoreOpen((open) => !open)}
            />
            <Collapse in={riskScoreOpen} animateOpacity>
              <Tabs variant="enclosed" size="sm">
                <TabList>
                  <Tab>Values</Tab>
                  <Tab>Explanations</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Metric</Th>
                          <Th isNumeric>Value</Th>
                          <Th isNumeric>Score (1-3)</Th>
                          <Th isNumeric>Weight (%)</Th>
                          <Th isNumeric>Weighted score</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {companyScoreView.valueRows.length === 0 && (
                          <Tr>
                            <Td colSpan={5}>No scorecard rows</Td>
                          </Tr>
                        )}
                        {companyScoreView.valueRows.map((row, index) => {
                          const divider = monitoringRowDividerProps(
                            index,
                            companyScoreView.valueRows.length,
                          );
                          return (
                            <Tr key={row.key} {...divider}>
                              <Td {...divider}>{row.metric}</Td>
                              <Td isNumeric {...divider}>
                                {row.value}
                              </Td>
                              <Td isNumeric {...divider}>
                                {row.score}
                              </Td>
                              <Td isNumeric {...divider}>
                                {row.weightPercent}
                              </Td>
                              <Td isNumeric {...divider}>
                                {row.weightedScore}
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                    {companyScoreView.totals.ratedCount > 0 && (
                      <Text fontSize="sm" color="gray.600" mt={3}>
                        Rated {companyScoreView.totals.ratedCount} /{' '}
                        {companyScoreView.totals.totalMetricCount}
                        {' · '}
                        Weight sum{' '}
                        {Number(
                          companyScoreView.totals.totalWeightPercent,
                        ).toFixed(2)}
                        {' · '}
                        Weighted score sum{' '}
                        {Number(
                          companyScoreView.totals.totalWeightedScore,
                        ).toFixed(4)}
                      </Text>
                    )}
                  </TabPanel>
                  <TabPanel px={0}>
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Metric</Th>
                          <Th>High (3)</Th>
                          <Th>Moderate (2)</Th>
                          <Th>Low (1)</Th>
                          <Th>Explanation of calculation</Th>
                          <Th>Calculation inputs</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {companyScoreView.explanationRows.map((row, index) => {
                          const divider = monitoringRowDividerProps(
                            index,
                            companyScoreView.explanationRows.length,
                          );
                          return (
                            <Tr key={row.key} {...divider}>
                              <Td {...divider}>{row.metric}</Td>
                              <Td {...divider} whiteSpace="normal" maxW="160px">
                                {row.high}
                              </Td>
                              <Td {...divider} whiteSpace="normal" maxW="160px">
                                {row.moderate}
                              </Td>
                              <Td {...divider} whiteSpace="normal" maxW="160px">
                                {row.low}
                              </Td>
                              <Td {...divider} whiteSpace="normal" maxW="280px">
                                {row.explanation}
                              </Td>
                              <Td {...divider} whiteSpace="normal" maxW="260px">
                                {row.calculationInputs}
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Collapse>
          </Card>

          <Card mt={6} variant="outline">
            <CollapsibleSectionHeader
              title={covenantsHeading}
              isOpen={covenantsOpen}
              onToggle={() => setCovenantsOpen((open) => !open)}
            />
            <Collapse in={covenantsOpen} animateOpacity>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Area</Th>
                    <Th>KPI</Th>
                    <Th isNumeric>Value</Th>
                    <Th>Constraint</Th>
                    <Th>Check</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {resultRows.map((row, index) => {
                    const divider = monitoringRowDividerProps(
                      index,
                      resultRows.length,
                    );
                    return (
                      <Tr key={`${row.area}-${row.kpi}-${index}`} {...divider}>
                        <Td {...divider}>{row.area}</Td>
                        <Td {...divider}>{row.kpi}</Td>
                        <Td isNumeric {...divider}>
                          {formatRowValue(row.kpi, row.value)}
                        </Td>
                        <Td {...divider}>
                          {formatMonitoringConstraintValue(row.constraint)}
                        </Td>
                        <Td {...divider}>{checkBadge(row.check)}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Collapse>
          </Card>

          <Card mt={6} variant="outline">
            <CollapsibleSectionHeader
              title="ROAS Cohort Performance"
              isOpen={roasCohortOpen}
              onToggle={() => setRoasCohortOpen((open) => !open)}
            />
            <HStack
              spacing={6}
              mb={roasCohortOpen ? 3 : 0}
              flexWrap="wrap"
              alignItems="center"
            >
              <RadioGroup value={roasTableView} onChange={setRoasTableView}>
                <HStack spacing={4}>
                  <Radio value="quick">Quick</Radio>
                  <Radio value="full">Full</Radio>
                </HStack>
              </RadioGroup>
            </HStack>
            <Collapse in={roasCohortOpen} animateOpacity>
              {roasRows.length === 0 ? (
                <Text>No ROAS cohort data</Text>
              ) : (
                <Box
                  overflowX="auto"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  {roasTableView === 'quick' ? (
                    <Table
                      size="sm"
                      variant="simple"
                      sx={{ borderCollapse: 'separate', borderSpacing: 0 }}
                    >
                      <Thead>
                        <Tr>
                          <Th
                            position="sticky"
                            left={0}
                            zIndex={3}
                            bg="gray.100"
                            borderBottomWidth="2px"
                            borderRightWidth="1px"
                            borderColor="gray.300"
                            minW={`${ROAS_COHORT_COL_W}px`}
                          >
                            Cohort
                          </Th>
                          <Th
                            position="sticky"
                            left={`${ROAS_COHORT_COL_W}px`}
                            zIndex={3}
                            bg="gray.100"
                            borderBottomWidth="2px"
                            borderRightWidth="1px"
                            borderColor="gray.300"
                            minW={`${ROAS_BASIS_COL_W}px`}
                          >
                            ROAS basis
                          </Th>
                          <Th
                            bg="gray.100"
                            borderBottomWidth="2px"
                            borderLeftWidth="1px"
                            borderColor="gray.300"
                            textAlign="right"
                            minW={`${ROAS_CAC_COL_W}px`}
                          >
                            CAC
                          </Th>
                          <Th
                            bg="gray.100"
                            borderBottomWidth="2px"
                            borderLeftWidth="1px"
                            borderColor="gray.300"
                            minW="120px"
                          >
                            Stage
                          </Th>
                          <Th
                            bg="gray.100"
                            borderBottomWidth="2px"
                            borderLeftWidth="1px"
                            borderColor="gray.300"
                            textAlign="center"
                            minW="88px"
                          >
                            {quickPrevMonthLabel}
                          </Th>
                          <Th
                            bg="gray.100"
                            borderBottomWidth="2px"
                            borderLeftWidth="1px"
                            borderColor="gray.300"
                            textAlign="center"
                            minW="88px"
                          >
                            {quickCurrMonthLabel}
                          </Th>
                          <Th
                            bg="gray.100"
                            borderBottomWidth="2px"
                            borderLeftWidth="1px"
                            borderColor="gray.300"
                            minW="88px"
                          >
                            Status
                          </Th>
                          <Th
                            bg="gray.100"
                            borderBottomWidth="2px"
                            borderLeftWidth="1px"
                            borderColor="gray.300"
                            minW="220px"
                          >
                            Why
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {roasQuickRows.map(({ row, quick }, rowIndex) => {
                          const divider = monitoringRowDividerProps(
                            rowIndex,
                            roasQuickRows.length,
                          );
                          const rowView = monitoringRoasRowView(row);
                          const renderQuickCell = (cell, key) => {
                            if (!cell) {
                              return (
                                <Td
                                  key={key}
                                  textAlign="center"
                                  borderLeftWidth="1px"
                                  borderColor="gray.200"
                                  px={2}
                                  {...divider}
                                >
                                  —
                                </Td>
                              );
                            }
                            const value = formatMonitoringRoasRowCellValue(cell, row);
                            const tip = monitoringRoasCellTooltip(
                              cell,
                              rowView,
                            );
                            const bg = monitoringRoasCellBackgroundColor(
                              cell,
                              rowView,
                            );
                            const color = monitoringRoasCellValueColor(
                              cell,
                              rowView,
                            );
                            const content = (
                              <HStack
                                spacing={1}
                                justify="center"
                                cursor={tip ? 'help' : 'default'}
                              >
                                <Text
                                  as="span"
                                  fontWeight={tip ? 700 : 500}
                                  color={color}
                                >
                                  {value}
                                </Text>
                                {cell.isPredicted ? (
                                  <Tooltip label="Predicted" hasArrow>
                                    <StarIcon boxSize={3} color="orange.400" />
                                  </Tooltip>
                                ) : null}
                              </HStack>
                            );
                            return (
                              <Td
                                key={key}
                                textAlign="center"
                                bg={bg}
                                borderLeftWidth="1px"
                                borderColor="gray.200"
                                px={2}
                                {...divider}
                              >
                                {tip ? (
                                  <Tooltip label={tip} hasArrow>
                                    {content}
                                  </Tooltip>
                                ) : (
                                  content
                                )}
                              </Td>
                            );
                          };
                          return (
                            <Tr
                              key={
                                row.rowGroupId ||
                                row.cohortLabel ||
                                row.cohortMonth ||
                                rowIndex
                              }
                              {...divider}
                            >
                              <Td
                                position="sticky"
                                left={0}
                                zIndex={1}
                                bg="white"
                                borderRightWidth="1px"
                                borderColor="gray.200"
                                {...divider}
                              >
                                <Text fontWeight="semibold">
                                  {row.cohortLabel || row.cohortMonth || '—'}
                                </Text>
                                {row.subtitle ? (
                                  <Text fontSize="xs" color="gray.500">
                                    {row.subtitle}
                                  </Text>
                                ) : null}
                              </Td>
                              <Td
                                position="sticky"
                                left={`${ROAS_COHORT_COL_W}px`}
                                zIndex={1}
                                bg="white"
                                borderRightWidth="1px"
                                borderColor="gray.200"
                                fontSize="sm"
                                fontWeight="semibold"
                                {...divider}
                              >
                                {formatMonitoringRoasCovenantBasis(row)}
                              </Td>
                              <Td
                                textAlign="right"
                                bg={monitoringRoasCacBackgroundColor(row)}
                                borderLeftWidth="1px"
                                borderColor="gray.200"
                                {...divider}
                              >
                                {(() => {
                                  const cacText = formatMonitoringRoasCac(
                                    row.cac,
                                  );
                                  const cacTip =
                                    monitoringRoasCacFails(row) &&
                                    row.cacIssueTooltip
                                      ? row.cacIssueTooltip
                                      : row.maxCac != null
                                        ? `Max CAC ${formatMonitoringRoasCac(row.maxCac)}`
                                        : null;
                                  const cacContent = (
                                    <Text
                                      as="span"
                                      fontWeight={
                                        monitoringRoasCacFails(row) ? 700 : 500
                                      }
                                      color={monitoringRoasCacValueColor(row)}
                                      cursor={cacTip ? 'help' : 'default'}
                                    >
                                      {cacText}
                                    </Text>
                                  );
                                  return cacTip ? (
                                    <Tooltip label={cacTip} hasArrow>
                                      {cacContent}
                                    </Tooltip>
                                  ) : (
                                    cacContent
                                  );
                                })()}
                              </Td>
                              <Td
                                borderLeftWidth="1px"
                                borderColor="gray.200"
                                {...divider}
                              >
                                {row.status || '—'}
                              </Td>
                              {renderQuickCell(
                                quick.previousCell,
                                `${row.rowGroupId}-prev`,
                              )}
                              {renderQuickCell(
                                quick.currentCell,
                                `${row.rowGroupId}-curr`,
                              )}
                              <Td
                                borderLeftWidth="1px"
                                borderColor="gray.200"
                                {...divider}
                              >
                                {quick.status === 'fail'
                                  ? checkBadge('fail')
                                  : quick.status === 'pass'
                                    ? checkBadge('pass')
                                    : checkBadge('n/a')}
                              </Td>
                              <Td
                                borderLeftWidth="1px"
                                borderColor="gray.200"
                                {...divider}
                              >
                                <Text fontSize="sm" color="gray.700">
                                  {quick.reason || '—'}
                                </Text>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  ) : (
                    <Table
                      size="sm"
                      variant="simple"
                      sx={{ borderCollapse: 'separate', borderSpacing: 0 }}
                    >
                      <Thead>
                        <Tr>
                          <Th
                            position="sticky"
                            left={0}
                            zIndex={3}
                            bg="gray.100"
                            borderBottomWidth="2px"
                            borderRightWidth="1px"
                            borderColor="gray.300"
                            minW={`${ROAS_COHORT_COL_W}px`}
                          >
                            Cohort
                          </Th>
                          <Th
                            position="sticky"
                            left={`${ROAS_COHORT_COL_W}px`}
                            zIndex={3}
                            bg="gray.100"
                            borderBottomWidth="2px"
                            borderRightWidth="1px"
                            borderColor="gray.300"
                            minW={`${ROAS_BASIS_COL_W}px`}
                          >
                            ROAS basis
                          </Th>
                          <Th
                            position="sticky"
                            left={`${ROAS_CAC_STICKY_LEFT}px`}
                            zIndex={3}
                            bg="gray.100"
                            borderBottomWidth="2px"
                            borderRightWidth="1px"
                            borderColor="gray.300"
                            textAlign="right"
                            minW={`${ROAS_CAC_COL_W}px`}
                          >
                            CAC
                          </Th>
                          <Th
                            position="sticky"
                            left={`${ROAS_STAGE_STICKY_LEFT}px`}
                            zIndex={3}
                            bg="gray.100"
                            borderBottomWidth="2px"
                            borderRightWidth="1px"
                            borderColor="gray.300"
                            minW="120px"
                          >
                            Stage
                          </Th>
                          {roasPeriodLabels.map((period) => (
                            <Th
                              key={period}
                              bg="gray.100"
                              borderBottomWidth="2px"
                              borderLeftWidth="1px"
                              borderColor="gray.300"
                              textAlign="center"
                              minW="72px"
                              px={2}
                            >
                              P{period}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {roasRows.map((row, rowIndex) => {
                          const divider = monitoringRowDividerProps(
                            rowIndex,
                            roasRows.length,
                          );
                          const cacText = formatMonitoringRoasCac(row.cac);
                          const cacTip =
                            monitoringRoasCacFails(row) && row.cacIssueTooltip
                              ? row.cacIssueTooltip
                              : row.maxCac != null
                                ? `Max CAC ${formatMonitoringRoasCac(row.maxCac)}`
                                : null;
                          const cacContent = (
                            <Text
                              as="span"
                              fontWeight={
                                monitoringRoasCacFails(row) ? 700 : 500
                              }
                              color={monitoringRoasCacValueColor(row)}
                              cursor={cacTip ? 'help' : 'default'}
                            >
                              {cacText}
                            </Text>
                          );
                          const rowView = monitoringRoasRowView(row);
                          return (
                            <Tr
                              key={
                                row.rowGroupId ||
                                row.cohortLabel ||
                                row.cohortMonth ||
                                rowIndex
                              }
                              {...divider}
                            >
                              <Td
                                position="sticky"
                                left={0}
                                zIndex={1}
                                bg="white"
                                borderRightWidth="1px"
                                borderColor="gray.200"
                                {...divider}
                              >
                                <Text fontWeight="semibold">
                                  {row.cohortLabel || row.cohortMonth || '—'}
                                </Text>
                                {row.subtitle ? (
                                  <Text fontSize="xs" color="gray.500">
                                    {row.subtitle}
                                  </Text>
                                ) : null}
                              </Td>
                              <Td
                                position="sticky"
                                left={`${ROAS_COHORT_COL_W}px`}
                                zIndex={1}
                                bg="white"
                                borderRightWidth="1px"
                                borderColor="gray.200"
                                fontSize="sm"
                                fontWeight="semibold"
                                {...divider}
                              >
                                {formatMonitoringRoasCovenantBasis(row)}
                              </Td>
                              <Td
                                position="sticky"
                                left={`${ROAS_CAC_STICKY_LEFT}px`}
                                zIndex={1}
                                textAlign="right"
                                bg={
                                  monitoringRoasCacBackgroundColor(row) ||
                                  'white'
                                }
                                borderRightWidth="1px"
                                borderColor="gray.200"
                                {...divider}
                              >
                                {cacTip ? (
                                  <Tooltip label={cacTip} hasArrow>
                                    {cacContent}
                                  </Tooltip>
                                ) : (
                                  cacContent
                                )}
                              </Td>
                              <Td
                                position="sticky"
                                left={`${ROAS_STAGE_STICKY_LEFT}px`}
                                zIndex={1}
                                bg="white"
                                borderRightWidth="1px"
                                borderColor="gray.200"
                                {...divider}
                              >
                                {row.status || '—'}
                              </Td>
                              {(row.cells || []).map((cell) => {
                                const value = formatMonitoringRoasRowCellValue(cell, row);
                                const tip = monitoringRoasCellTooltip(
                                  cell,
                                  rowView,
                                );
                                const bg = monitoringRoasCellBackgroundColor(
                                  cell,
                                  rowView,
                                );
                                const color = monitoringRoasCellValueColor(
                                  cell,
                                  rowView,
                                );
                                const content = (
                                  <HStack
                                    spacing={1}
                                    justify="center"
                                    cursor={tip ? 'help' : 'default'}
                                  >
                                    <Text
                                      as="span"
                                      fontWeight={tip ? 700 : 500}
                                      color={color}
                                    >
                                      {value}
                                    </Text>
                                    {cell.isPredicted ? (
                                      <Tooltip label="Predicted" hasArrow>
                                        <StarIcon
                                          boxSize={3}
                                          color="orange.400"
                                        />
                                      </Tooltip>
                                    ) : null}
                                  </HStack>
                                );
                                return (
                                  <Td
                                    key={`${row.rowGroupId || row.cohortLabel}-${cell.period}`}
                                    textAlign="center"
                                    bg={bg}
                                    borderLeftWidth="1px"
                                    borderColor="gray.200"
                                    px={2}
                                    {...divider}
                                  >
                                    {tip ? (
                                      <Tooltip label={tip} hasArrow>
                                        {content}
                                      </Tooltip>
                                    ) : (
                                      content
                                    )}
                                  </Td>
                                );
                              })}
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  )}
                </Box>
              )}
            </Collapse>
          </Card>
        </>
      )}

      <Modal isOpen={isSaveOpen} onClose={onSaveClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save monitoring report</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="Report name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onSaveClose}>
              Cancel
            </Button>
            <Button
              variant="brand"
              onClick={onSave}
              isLoading={isSaving}
              isDisabled={!saveName.trim()}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Monitoring;
